/* Copyright (c) 2026 Hyegro Pty Ltd. All rights reserved. */

/**
 * Account 360, the v2 account record (docs/ui-v2/ACCOUNT_360.md).
 *
 * Layout only. AccountDetail keeps every handler, effect and modal; it hands
 * this component its data, its action buttons, its field sections and its
 * side-column cards, and this places them: a hero, headline figures, tabs for
 * everything related to the account, and an interaction timeline beside.
 *
 * The tenant's vertical decides the dressing (accent, names, which tabs come
 * first). It never grants a tab: see account360Tabs.js.
 */

import { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { useNavigate, useSearchParams } from "react-router-dom";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import Typography from "@mui/material/Typography";
import { alpha } from "@mui/material/styles";
import {
  Activity,
  BedDouble,
  Briefcase,
  CalendarClock,
  CircleDollarSign,
  Clock,
  Eye,
  FileText,
  GraduationCap,
  Headset,
  HeartPulse,
  House,
  KeyRound,
  Landmark,
  LayoutGrid,
  LifeBuoy,
  Paperclip,
  Plane,
  Receipt,
  ShoppingCart,
  Star,
  Store,
  Target,
  Ticket,
  Users,
  Wallet,
} from "lucide-react";
import { useAuth } from "auth/Auth";
import { useModuleEntitlements } from "hooks/useModuleEntitlements";
import { useActiveApp } from "shell-v2/apps";
import { fetchAccountInvoices } from "api/customer360";
import { v2 as t, v2EditionAccents } from "styles/tokens/v2";
import Account360Hero from "./Account360Hero";
import Account360KpiStrip from "./Account360KpiStrip";
import ActivityTimeline from "./ActivityTimeline";
import RelatedList from "./RelatedList";
import { buildAccount360Tabs, resolveVerticalApp } from "./account360Tabs";
import useAccount360Summary from "./useAccount360Summary";
import { formatMoney, headlineMoney, relativeTime } from "./format";

const APP_ICONS = { Target, Headset, Plane, House, GraduationCap, HeartPulse };

const TAB_ICONS = {
  overview: LayoutGrid,
  contacts: Users,
  deals: Briefcase,
  cases: LifeBuoy,
  orders: ShoppingCart,
  invoices: Receipt,
  bookings: Ticket,
  travellers: Plane,
  "travel-invoices": Receipt,
  supplier: Store,
  "re-bookings": KeyRound,
  viewings: Eye,
  leases: FileText,
  mortgages: Landmark,
  activity: Activity,
  files: Paperclip,
};

const ACCOUNT_NOUN = { REAL_ESTATE: "Vendor or landlord", HIGHER_ED: "Education agent" };

const CLOSED_CASE = /closed|resolved|cancel/i;
const CLOSED_DEAL = /closed won|closed lost/i;

function money(list, preferred, compact = true) {
  const h = headlineMoney(list, preferred);
  if (!h) return null;
  return {
    value: formatMoney(h.main.amount, h.main.currency, { compact }),
    note: h.others.length ? `+${h.others.length} more ${h.others.length === 1 ? "currency" : "currencies"}` : null,
    noteTitle: h.others.length ? h.others.map((m) => formatMoney(m.amount, m.currency)).join(" · ") : null,
  };
}

const fmtDate = (v) => (v ? new Date(v).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" }) : null);
const join = (...parts) => parts.filter(Boolean).join(" · ");

function Stack({ children }) {
  return <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5, "& > .MuiBox-root:empty": { display: "none" } }}>{children}</Box>;
}
Stack.propTypes = { children: PropTypes.node.isRequired };

function InvoicesPanel({ accountId, accent }) {
  const { axiosInstance } = useAuth();
  const [rows, setRows] = useState(null);
  useEffect(() => {
    let cancelled = false;
    fetchAccountInvoices(accountId, axiosInstance, { showTravel: false }).then((list) => {
      if (!cancelled) setRows(list);
    });
    return () => {
      cancelled = true;
    };
  }, [accountId, axiosInstance]);
  return (
    <RelatedList
      title="Invoices"
      icon={Receipt}
      accent={accent}
      loading={rows === null}
      emptyText="No invoices for this account yet."
      rows={(rows || []).map((i) => ({
        id: i.id,
        primary: i.invoice_number || `Invoice #${i.id}`,
        secondary: join(fmtDate(i.invoice_date), i.due_date ? `Due ${fmtDate(i.due_date)}` : null, i.order_number),
        amount: i.total_amount != null ? formatMoney(i.total_amount, i.currency) : null,
        status: i.payment_status || i.status,
        to: `/invoices/${i.id}`,
      }))}
    />
  );
}
InvoicesPanel.propTypes = { accountId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired, accent: PropTypes.object.isRequired };

function KeyPeople({ contacts, label, accent, onOpen }) {
  const people = [...contacts]
    .sort((a, b) => Number(Boolean(b.details?.primary_contact)) - Number(Boolean(a.details?.primary_contact)))
    .slice(0, 4);
  if (!people.length) return null;
  return (
    <Card sx={{ p: 0, overflow: "hidden" }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, px: 2.25, py: 1.75, borderBottom: `1px solid ${t.border}` }}>
        <Users size={17} color={t.text2} aria-hidden="true" />
        <Typography component="h2" sx={{ fontSize: 15, fontWeight: 600, color: t.text, flex: 1 }}>
          Key {label.toLowerCase()}
        </Typography>
      </Box>
      <Box component="ul" sx={{ listStyle: "none", m: 0, p: 1 }}>
        {people.map((c) => {
          const initials = (c.name || "?").split(/\s+/).map((w) => w[0]).join("").slice(0, 2).toUpperCase();
          return (
            <li key={c.details?.id || c.name}>
              <Box
                component="button"
                type="button"
                onClick={() => onOpen(c)}
                sx={{ width: "100%", display: "flex", alignItems: "center", gap: 1.25, p: 1, border: 0, borderRadius: "8px", bgcolor: "transparent", font: "inherit", textAlign: "left", cursor: "pointer", "&:hover": { bgcolor: t.hover }, "&:focus-visible": { outline: `2px solid ${t.link}` } }}
              >
                <Box sx={{ width: 34, height: 34, borderRadius: "50%", display: "grid", placeItems: "center", flexShrink: 0, fontSize: 13, fontWeight: 700, bgcolor: accent.tint, color: accent.ink }}>
                  {initials}
                </Box>
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography sx={{ fontSize: 13.5, fontWeight: 600, color: t.text, display: "flex", alignItems: "center", gap: 0.5 }}>
                    <Box component="span" sx={{ whiteSpace: "normal", wordBreak: "break-word", overflowWrap: "anywhere", lineHeight: 1.35 }}>{c.name}</Box>
                    {c.details?.primary_contact && <Star size={13} fill="#E0A100" color="#B45309" aria-label="Primary contact" />}
                  </Typography>
                  <Typography sx={{ fontSize: 12.5, color: t.text3, whiteSpace: "normal", wordBreak: "break-word", overflowWrap: "anywhere", lineHeight: 1.35 }}>
                    {c.details?.title || c.email || c.mobile || ""}
                  </Typography>
                </Box>
              </Box>
            </li>
          );
        })}
      </Box>
    </Card>
  );
}
KeyPeople.propTypes = { contacts: PropTypes.array.isRequired, label: PropTypes.string.isRequired, accent: PropTypes.object.isRequired, onOpen: PropTypes.func.isRequired };

export default function Account360V2({
  accountId,
  account,
  agents,
  contacts,
  deals,
  cases,
  activities,
  feedItems,
  attachments,
  isCloning,
  recordActions,
  recordDetailsBody,
  cards,
  gates,
}) {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { isModuleEnabled, hasEdition, apps, defaultApp } = useModuleEntitlements();
  const { app: activeApp } = useActiveApp();

  const vertical = resolveVerticalApp(activeApp, apps, defaultApp);
  const labels = vertical?.labels || {};
  // PearlCRM brand blue in every edition; the vertical shows through its
  // badge, icon and tab names rather than a colour of its own.
  const accent = useMemo(() => {
    const { accent: main, ink } = v2EditionAccents.sales;
    return { main, ink, tint: alpha(main, 0.1) };
  }, []);
  const VerticalIcon = vertical ? APP_ICONS[vertical.icon] || null : null;

  const { summary, travel, realEstate } = useAccount360Summary(accountId, {
    travel: Boolean(gates.showTravel),
    realEstate: Boolean(hasEdition && hasEdition("REAL_ESTATE")),
  });

  const tabs = buildAccount360Tabs({
    labels,
    verticalCode: vertical?.code || null,
    isModuleEnabled,
    gates,
    summary,
    travel,
    realEstate,
    local: { contacts: contacts.length, deals: deals.length, cases: cases.length, attachments: attachments.length },
  });
  const requested = searchParams.get("tab");
  const current = tabs.some((tb) => tb.key === requested) ? requested : "overview";
  const selectTab = (key) => {
    const next = new URLSearchParams(searchParams);
    if (key === "overview") next.delete("tab");
    else next.set("tab", key);
    setSearchParams(next, { replace: true });
  };

  const ownerName = agents.find((a) => a.id === account?.manager_id)?.fullname || account?.manager_name || null;
  const preferred = summary?.currency || account?.currency;

  // ---- headline figures --------------------------------------------------
  const tiles = [];
  const c = summary?.counts || {};
  const m = summary?.money || {};
  const contactsLabel = labels.contacts || "Contacts";

  if (travel?.bookings) {
    const v = money(travel.bookings.value, preferred);
    tiles.push({ key: "bookings", icon: Ticket, label: "Bookings", value: String(travel.bookings.count), note: v ? `${v.value} booked` : null, noteTitle: v?.noteTitle, onClick: () => selectTab("bookings") });
  }
  if (travel?.travellers) {
    tiles.push({ key: "travellers", icon: Plane, label: labels["travel-contacts"] || "Travellers", value: String(travel.travellers.count), onClick: () => selectTab("travellers") });
  }
  if (realEstate?.bookings) {
    const v = money(realEstate.bookings.value, preferred);
    const due = money(realEstate.bookings.due, preferred);
    tiles.push({ key: "re-value", icon: KeyRound, label: "Property bookings", value: v ? v.value : String(realEstate.bookings.count), note: due ? `${due.value} still due` : `${realEstate.bookings.count} booking${realEstate.bookings.count === 1 ? "" : "s"}`, noteTitle: v?.noteTitle, onClick: () => selectTab("re-bookings") });
  }
  if (realEstate?.leases) {
    tiles.push({ key: "leases", icon: FileText, label: "Active leases", value: String(realEstate.leases.active ?? realEstate.leases.count), note: `${realEstate.leases.count} in total`, onClick: () => selectTab("leases") });
  }
  if (realEstate?.viewings) {
    tiles.push({ key: "viewings", icon: Eye, label: "Upcoming viewings", value: String(realEstate.viewings.upcoming), onClick: () => selectTab("viewings") });
  }

  const contactCount = c.contacts ?? contacts.length;
  tiles.push({ key: "contacts", icon: Users, label: contactsLabel, value: String(contactCount), onClick: () => selectTab("contacts") });

  if (isModuleEnabled("DEALS")) {
    const openDeals = c.deals_open ?? deals.filter((d) => !CLOSED_DEAL.test(d.stage || "")).length;
    const pipe = money(m.pipeline, preferred);
    tiles.push({
      key: "pipeline",
      icon: CircleDollarSign,
      label: `Open ${(labels.deals || "Deals").toLowerCase()}`,
      value: pipe ? pipe.value : String(openDeals),
      note: pipe ? `${openDeals} open${pipe.note ? ` · ${pipe.note}` : ""}` : null,
      noteTitle: pipe?.noteTitle,
      onClick: () => selectTab("deals"),
    });
  }
  if (isModuleEnabled("CASES")) {
    const openCases = c.cases_open ?? cases.filter((k) => !CLOSED_CASE.test(k.status || "")).length;
    tiles.push({ key: "cases", icon: LifeBuoy, label: `Open ${(labels.cases || "Cases").toLowerCase()}`, value: String(openCases), onClick: () => selectTab("cases") });
  }
  const outstandingList = gates.showTravel ? travel?.invoices?.outstanding : m.outstanding;
  if (Array.isArray(outstandingList)) {
    const o = money(outstandingList, preferred);
    tiles.push({ key: "outstanding", icon: Wallet, label: "Outstanding", value: o ? o.value : formatMoney(0, preferred), note: o?.note, noteTitle: o?.noteTitle, onClick: () => selectTab(gates.showTravel ? "travel-invoices" : "invoices") });
  }
  const lastActivity = summary?.last_activity_at || activities.map((a) => a?.created_at).filter(Boolean).sort().pop();
  tiles.push({ key: "last", icon: Clock, label: "Last activity", value: relativeTime(lastActivity) || "None yet", onClick: () => selectTab("activity") });

  // ---- panels ------------------------------------------------------------
  const panel = (() => {
    switch (current) {
      case "contacts":
        return cards.contacts;
      case "deals":
        return cards.deals;
      case "cases":
        return cards.cases;
      case "orders":
        return cards.orders;
      case "invoices":
        return <InvoicesPanel accountId={accountId} accent={accent} />;
      case "activity":
        return (
          <Stack>
            {cards.activities}
            {cards.echoFeed}
            {cards.callHistory}
          </Stack>
        );
      case "files":
        return cards.attachments;
      case "supplier":
        return cards.merchant;
      case "bookings":
        return (
          <RelatedList
            title={labels["travel-orders"] || "Bookings"}
            icon={Ticket}
            accent={accent}
            emptyText="No bookings for this account yet."
            footer={travel?.bookings?.count > travel?.bookings?.recent?.length ? `Showing the latest ${travel.bookings.recent.length} of ${travel.bookings.count}.` : null}
            rows={(travel?.bookings?.recent || []).map((b) => ({
              id: b.id,
              primary: b.name || `Booking #${b.id}`,
              secondary: join(fmtDate(b.order_date), `${b.pax_count} traveller${b.pax_count === 1 ? "" : "s"}`, b.payment_status),
              amount: b.order_total != null ? formatMoney(b.order_total, b.currency) : null,
              status: b.status,
              to: `/travel-order/${b.id}`,
            }))}
          />
        );
      case "travellers":
        return (
          <RelatedList
            title={labels["travel-contacts"] || "Travellers"}
            icon={BedDouble}
            accent={accent}
            emptyText="No travellers linked to this account yet."
            rows={(travel?.travellers?.recent || []).map((p) => ({
              id: `${p.source}-${p.id}`,
              primary: p.name || "Traveller",
              secondary: join(p.email, p.travel_order_id ? `Booking #${p.travel_order_id}` : "Saved traveller"),
              to: p.source === "travel_contact" ? `/travel-contact/${p.id}` : p.travel_order_id ? `/travel-order/${p.travel_order_id}` : undefined,
            }))}
          />
        );
      case "travel-invoices":
        return (
          <RelatedList
            title="Invoices"
            icon={Receipt}
            accent={accent}
            emptyText="No invoices for this account yet."
            rows={(travel?.invoices?.recent || []).map((i) => ({
              id: i.id,
              primary: i.invoice_number || `Invoice #${i.id}`,
              secondary: i.due_date ? `Due ${fmtDate(i.due_date)}` : null,
              amount: i.total_amount != null ? formatMoney(i.total_amount, i.currency) : null,
              status: i.payment_status || i.status,
              to: `/invoices/${i.id}`,
            }))}
          />
        );
      case "re-bookings":
        return (
          <RelatedList
            title="Property bookings"
            icon={KeyRound}
            accent={accent}
            emptyText="No property bookings for this account's people yet."
            rows={(realEstate?.bookings?.recent || []).map((b) => ({
              id: b.id,
              primary: b.booking_number || `Booking #${b.id}`,
              secondary: join(fmtDate(b.booking_date), b.amount_due != null ? `${formatMoney(b.amount_due, b.currency)} due` : null),
              amount: b.net_price != null ? formatMoney(b.net_price, b.currency) : null,
              status: b.status,
              to: `/real-estate/bookings/${b.id}`,
            }))}
          />
        );
      case "viewings":
        return (
          <RelatedList
            title="Viewings"
            icon={CalendarClock}
            accent={accent}
            emptyText="No viewings booked for this account's people."
            rows={(realEstate?.viewings?.recent || []).map((v) => ({
              id: v.id,
              primary: v.scheduled_at ? new Date(v.scheduled_at).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }) : `Viewing #${v.id}`,
              secondary: v.viewing_type,
              status: v.status,
              to: `/real-estate/viewings/${v.id}`,
            }))}
          />
        );
      case "leases":
        return (
          <RelatedList
            title="Leases"
            icon={FileText}
            accent={accent}
            emptyText="No leases for this account."
            rows={(realEstate?.leases?.recent || []).map((l) => ({
              id: l.id,
              primary: l.lease_number || `Lease #${l.id}`,
              secondary: join(fmtDate(l.start_date), l.end_date ? `to ${fmtDate(l.end_date)}` : null),
              amount: l.annual_rent != null ? `${formatMoney(l.annual_rent, l.currency)} / yr` : null,
              status: l.status,
              to: `/real-estate/leases/${l.id}`,
            }))}
          />
        );
      case "mortgages":
        return (
          <RelatedList
            title="Mortgage applications"
            icon={Landmark}
            accent={accent}
            emptyText="No mortgage applications for this account's people."
            rows={(realEstate?.mortgages?.recent || []).map((a) => ({
              id: a.id,
              primary: a.application_number || `Application #${a.id}`,
              amount: a.loan_amount != null ? formatMoney(a.loan_amount, a.currency) : null,
              status: a.stage,
              to: `/real-estate/mortgage-applications/${a.id}`,
            }))}
          />
        );
      default:
        return (
          // Each field section is its own card (DetailSection), so no wrapping Card.
          <Box sx={{ animation: isCloning ? "cloneAnimation 1s ease" : "none" }}>{recordDetailsBody}</Box>
        );
    }
  })();

  const roleBadges = (realEstate?.party_roles || []).map((role) => (
    <Box key={role} component="span" sx={{ display: "inline-flex", alignItems: "center", height: 24, px: 1.1, borderRadius: "12px", fontSize: 12.5, fontWeight: 600, bgcolor: accent.tint, color: accent.ink }}>
      {role === "developer" ? "Developer" : role === "broker_firm" ? "Broker firm" : role}
    </Box>
  ));

  return (
    <Box>
      <Account360Hero
        account={account}
        ownerName={ownerName}
        ownerId={ownerName ? account?.manager_id ?? null : null}
        eyebrow={ACCOUNT_NOUN[vertical?.code] || (account?.account_type === "Partner" ? "Partner account" : "Account")}
        accent={accent}
        verticalName={vertical?.name || null}
        VerticalIcon={VerticalIcon}
        extraBadges={roleBadges}
        actions={recordActions}
      />

      <Account360KpiStrip tiles={tiles.slice(0, 6)} accent={accent} />

      <Grid container spacing={2.5}>
        <Grid item xs={12} lg={8}>
          <Card sx={{ p: 0, mb: 2.5, overflow: "hidden" }}>
            <Tabs
              value={current}
              onChange={(_e, key) => selectTab(key)}
              variant="scrollable"
              scrollButtons="auto"
              allowScrollButtonsMobile
              aria-label="Account sections"
              sx={{
                px: 1,
                minHeight: 48,
                "& .MuiTabs-indicator": { bgcolor: accent.main, height: 3, borderRadius: "3px 3px 0 0" },
                "& .MuiTab-root": { minHeight: 48, px: 1.5, gap: 0.75, color: t.text2, fontWeight: 500 },
                "& .MuiTab-root.Mui-selected": { color: accent.ink, fontWeight: 600 },
              }}
            >
              {tabs.map((tb) => {
                const Icon = TAB_ICONS[tb.key] || LayoutGrid;
                return (
                  <Tab
                    key={tb.key}
                    value={tb.key}
                    iconPosition="start"
                    icon={<Icon size={16} aria-hidden="true" />}
                    label={
                      <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 0.75 }}>
                        {tb.label}
                        {typeof tb.count === "number" && (
                          <Box
                            component="span"
                            sx={{
                              minWidth: 20,
                              height: 20,
                              px: 0.6,
                              borderRadius: "10px",
                              fontSize: 11.5,
                              fontWeight: 700,
                              display: "inline-grid",
                              placeItems: "center",
                              bgcolor: current === tb.key ? accent.tint : t.surface2,
                              color: current === tb.key ? accent.ink : t.text2,
                            }}
                          >
                            {tb.count}
                          </Box>
                        )}
                      </Box>
                    }
                  />
                );
              })}
            </Tabs>
          </Card>
          <Box role="tabpanel" aria-label={tabs.find((tb) => tb.key === current)?.label}>
            {panel}
          </Box>
        </Grid>

        <Grid item xs={12} lg={4}>
          <Box sx={{ position: { lg: "sticky" }, top: { lg: 16 }, display: "flex", flexDirection: "column", gap: 2.5 }}>
            <ActivityTimeline activities={activities} feedItems={feedItems} onOpenAll={() => selectTab("activity")} />
            <KeyPeople
              contacts={contacts}
              label={contactsLabel}
              accent={accent}
              onOpen={(person) => person.details?.id && navigate(`/contacts/${person.details.id}`)}
            />
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
}

Account360V2.propTypes = {
  accountId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  account: PropTypes.object,
  agents: PropTypes.array,
  contacts: PropTypes.array,
  deals: PropTypes.array,
  cases: PropTypes.array,
  activities: PropTypes.array,
  feedItems: PropTypes.array,
  attachments: PropTypes.array,
  isCloning: PropTypes.bool,
  recordActions: PropTypes.node,
  recordDetailsBody: PropTypes.node,
  cards: PropTypes.shape({
    activities: PropTypes.node,
    attachments: PropTypes.node,
    callHistory: PropTypes.node,
    echoFeed: PropTypes.node,
    contacts: PropTypes.node,
    deals: PropTypes.node,
    orders: PropTypes.node,
    cases: PropTypes.node,
    merchant: PropTypes.node,
  }).isRequired,
  gates: PropTypes.shape({
    showTravel: PropTypes.bool,
    showLegacyOrders: PropTypes.bool,
    showMerchant: PropTypes.bool,
  }).isRequired,
};

Account360V2.defaultProps = {
  account: null,
  agents: [],
  contacts: [],
  deals: [],
  cases: [],
  activities: [],
  feedItems: [],
  attachments: [],
  isCloning: false,
  recordActions: null,
  recordDetailsBody: null,
};
